import {useRouteError, useNavigate} from "react-router-dom";
import * as Sentry from "@sentry/react";
import {useEffect} from "react";
import {motion} from "motion/react";
import {AlertTriangle, Home, RefreshCw} from "lucide-react";
import {Card, CardContent, CardFooter, CardHeader} from "@/components/ui/card";
import {Button} from "@/components/ui/button";
import * as m from "@/paraglide/messages";

export function RouterErrorBoundary() {
  const error = useRouteError() as Error;
  const navigate = useNavigate();

  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  const handleGoHome = () => {
    navigate("/");
  };

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <div className="bg-background flex min-h-dvh items-center justify-center p-4">
      <motion.div
        initial={{opacity: 0, y: 24}}
        animate={{opacity: 1, y: 0}}
        transition={{duration: 0.5, ease: [0.22, 1, 0.36, 1]}}
        className="w-full max-w-md"
      >
        <Card className="border-border/50 bg-card/80 shadow-xl backdrop-blur-sm">
          <CardHeader className="pb-4 text-center">
            <motion.div
              initial={{scale: 0.8, opacity: 0}}
              animate={{scale: 1, opacity: 1}}
              transition={{
                type: "spring",
                stiffness: 200,
                damping: 15,
                delay: 0.1,
              }}
              className="bg-destructive/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
            >
              <AlertTriangle className="text-destructive h-8 w-8" strokeWidth={1.5} />
            </motion.div>
            <motion.h1
              initial={{opacity: 0, y: 8}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: 0.2, duration: 0.4}}
              className="text-xl font-semibold tracking-tight"
            >
              {m.error_something_wrong()}
            </motion.h1>
          </CardHeader>

          <CardContent className="pb-6 text-center">
            <motion.p
              initial={{opacity: 0, y: 8}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: 0.3, duration: 0.4}}
              className="text-muted-foreground"
            >
              {m.error_unexpected_description()}
            </motion.p>
          </CardContent>

          <CardFooter className="flex flex-col gap-3 sm:flex-row">
            <motion.div
              initial={{opacity: 0, y: 8}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: 0.4, duration: 0.4}}
              className="w-full sm:w-1/2"
            >
              <Button variant="outline" onClick={handleRetry} className="w-full">
                <RefreshCw className="mr-2 h-4 w-4" />
                {m.error_try_again()}
              </Button>
            </motion.div>
            <motion.div
              initial={{opacity: 0, y: 8}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: 0.5, duration: 0.4}}
              className="w-full sm:w-1/2"
            >
              <Button onClick={handleGoHome} className="w-full">
                <Home className="mr-2 h-4 w-4" />
                {m.error_go_home()}
              </Button>
            </motion.div>
          </CardFooter>
        </Card>
      </motion.div>
    </div>
  );
}
